interface WhyChooseSectionProps {
  isVisible: boolean;
  sectionRef: React.RefObject<HTMLElement | null>;
  name?: string;
  description?: string;
  background?: string;
  logo?: string;
  nameColor?: string;
  descriptionColor?: string;
  items?: any[]; // Nhận items từ props thay vì tự call API
}

interface WhyChooseItem {
  id: string;
  icon: string;
  title: string;
  description: string;
  iconBg?: string;
  color?: string;
  link?: string;
}

export default function WhyChooseSection({
  isVisible,
  sectionRef,
  name = 'Tại Sao Chọn StackWay?',
  description = 'Chúng tôi không chỉ là đối tác công nghệ mà còn là người đồng hành tin cậy trong hành trình phát triển doanh nghiệp của bạn',
  background,
  logo = `${typeof window !== 'undefined' ? window.location.origin : ''}/stackway/logo/logo.png`,
  nameColor,
  descriptionColor,
  items = [] // Nhận từ props
}: WhyChooseSectionProps) {
  const reasons: WhyChooseItem[] = items || [];
  const loading = false; // Không cần loading vì đã có data từ props
  
  const backgroundStyle = background
    ? {
        backgroundImage: background.startsWith('http') || background.startsWith('/')
          ? `url(${background})`
          : undefined,
        backgroundColor: background.startsWith('http') || background.startsWith('/') ? undefined : background,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : undefined;

  return (
    <section
      ref={sectionRef}
      data-section="whyChoose"
      className={`py-12 relative overflow-hidden ${
        !background ? "bg-gradient-to-b from-gray-50 via-white to-gray-50" : ""
      }`}
      style={backgroundStyle}
    >
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#00c897]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#2a4b7c]/20 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10 mt-4">
        <div
          className={`text-center mb-0 transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
          }`}
        >
          <h2 
            className="text-4xl font-bold mb-1"
            style={{ color: nameColor || '#333' }}
          >
            {name}
          </h2>
          {description && (
            <p 
              className="text-lg max-w-2xl mx-auto"
              style={{ color: descriptionColor || '#4b5563' }}
            >
              {description}
            </p>
          )}
        </div>

        {/* Desktop Layout - Circular */}
        <div className="relative max-w-6xl mx-auto hidden lg:block -mt-16">
          {/* Center Logo - Always in the middle of circle */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
            <img
              src={logo}
              alt="StackWay Logo"
              className="h-32 w-auto"
            />
          </div>

          {/* STACKWAY Text Below Logo */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 mt-24 z-10">
            <div className="text-4xl font-extrabold bg-gradient-to-r from-[#2a4b7c] via-[#00c897] to-[#2a4b7c] bg-clip-text text-transparent bg-[length:200%_auto] animate-text-gradient">
              STACKWAY
            </div>
          </div>

          {/* Circular Border - 2/3 circle */}
          <div className="relative w-full aspect-square max-w-2xl mx-auto -mt-8">
            <svg className="w-full h-full" viewBox="0 0 400 400">
              {/* 2/3 Circle (240 degrees) */}
              <circle
                cx="200"
                cy="200"
                r="120"
                fill="none"
                stroke="#00c897"
                strokeWidth="2"
                strokeDasharray="5,5"
                strokeDashoffset="0"
                pathLength="360"
                style={{
                  strokeDasharray: "240, 120",
                  transform: "rotate(-30deg)",
                  transformOrigin: "center",
                }}
                className="animate-spin-slow"
              />

              {/* Dots on circle */}
              <circle cx="304" cy="200" r="4" fill="#00c897" />
              <circle cx="200" cy="320" r="4" fill="#00c897" />
              <circle cx="96" cy="200" r="4" fill="#00c897" />
            </svg>

            {/* Reason Cards - Outside Circle with More Space */}
            {loading ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="inline-block w-16 h-16 border-4 border-[#2a4b7c] border-t-transparent rounded-full animate-spin"></div>
                  <p className="mt-4 text-gray-600">Đang tải...</p>
                </div>
              </div>
            ) : reasons.length > 0 ? (
              <div className="absolute inset-0">
                {/* Dynamic positioning for reasons */}
                {reasons.map((reason, index) => {
                  // Position calculation: 4 items around circle
                  // Top Left, Bottom Left, Top Right, Bottom Right
                  const positions = [
                    { top: 'top-1/4', left: '-left-72', transform: '-translate-y-1/2' },
                    { top: 'bottom-1/4', left: '-left-72', transform: 'translate-y-1/2' },
                    { top: 'top-[20%]', left: '-right-72', transform: '-translate-y-1/2' },
                    { top: 'bottom-[28%]', left: '-right-72', transform: 'translate-y-1/2' }
                  ];
                  const position = positions[index] || positions[0];
                  const ContentWrapper = reason.link ? 'a' : 'div';
                  
                  return (
                    <div
                      key={reason.id}
                      className={`absolute ${position.top} ${position.left} ${position.transform} transition-all duration-700 ${
                        isVisible
                          ? "opacity-100 translate-y-0"
                          : "opacity-0 -translate-y-8"
                      }`}
                      style={{ transitionDelay: `${(index + 1) * 100}ms` }}
                    >
                      <ContentWrapper
                        href={reason.link || undefined}
                        className={`bg-white rounded-xl shadow-lg p-6 w-96 border border-gray-100 hover:border-[#00c897] transition-all duration-300 hover:shadow-xl group ${reason.link ? 'cursor-pointer' : ''}`}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`w-12 h-12 flex items-center justify-center ${reason.iconBg || 'bg-[#2a4b7c]'} rounded-lg group-hover:bg-[#00c897] transition-all duration-300`}>
                            <i className={`${reason.icon} text-2xl ${reason.color || 'text-white'}`}></i>
                          </div>
                        </div>
                        <h3 className="text-base font-bold text-[#333] mb-2 group-hover:text-[#00c897] transition-colors">
                          {reason.title}
                        </h3>
                        <p className="text-sm text-[#4b5563]">
                          {reason.description}
                        </p>
                      </ContentWrapper>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>

        {/* Mobile Layout - Vertical Stack */}
        <div className="lg:hidden space-y-2">
          {/* Logo and Title */}
          <div
            className={`text-center mb-1 transition-all duration-700 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
            }`}
          >
            <img
              src={logo}
              alt="StackWay Logo"
              className="h-24 w-auto mx-auto mb-4"
            />
            <div className="text-3xl font-extrabold bg-gradient-to-r from-[#2a4b7c] via-[#00c897] to-[#2a4b7c] bg-clip-text text-transparent">
              STACKWAY
            </div>
          </div>

          {/* Reason Cards - Vertical Stack */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-16 h-16 border-4 border-[#2a4b7c] border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-gray-600">Đang tải...</p>
            </div>
          ) : reasons.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Chưa có nội dung</p>
            </div>
          ) : (
            reasons.map((reason, index) => {
              const ContentWrapper = reason.link ? 'a' : 'div';
              return (
                <div
                  key={reason.id}
                  className={`bg-white rounded-xl shadow-lg p-6 border border-gray-100 transition-all duration-700 ${
                    isVisible
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-8"
                  }`}
                  style={{ transitionDelay: `${(index + 1) * 100}ms` }}
                >
                  <ContentWrapper
                    href={reason.link || undefined}
                    className={`flex items-start gap-4 ${reason.link ? 'cursor-pointer' : ''}`}
                  >
                    <div className={`w-12 h-12 flex items-center justify-center ${reason.iconBg || 'bg-[#00c897]'} rounded-lg flex-shrink-0`}>
                      <i className={`${reason.icon} text-2xl ${reason.color || 'text-white'}`}></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-[#333] mb-2">
                        {reason.title}
                      </h3>
                      <p className="text-sm text-[#4b5563]">
                        {reason.description}
                      </p>
                    </div>
                  </ContentWrapper>
                </div>
              );
            })
          )}
        </div>

        {/* CTA Button */}
        <div
          className={`text-center -mt-4 transition-all duration-1000 delay-500 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
          }`}
        >
          <Link
            to="/ve-chung-toi"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#00c897] text-white text-base font-semibold rounded-lg hover:bg-[#00b587] transition-all duration-300 whitespace-nowrap hover:scale-105 hover:shadow-xl shadow-lg shadow-[#00c897]/30"
          >
            Tìm hiểu thêm về chúng tôi
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}

